// src/components/CihazDetay.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { Box, Typography, Grid, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DevicesIcon from '@mui/icons-material/Devices';
import { formatDate, formatCurrency, formatDateTime } from '../utils/formatters';
import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import { Loading } from '../components/common/Loading';

const CihazDetay = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [serviceHistory, setServiceHistory] = useState([]);
    const [deviceLogs, setDeviceLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userMap, setUserMap] = useState({});

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersSnapshot = await getDocs(collection(db, "users")); // getDocs import edilmeli
                const map = {};
                usersSnapshot.forEach(doc => {
                    const data = doc.data();
                    map[doc.id] = data.username || data.email || "Bilinmeyen Kullanıcı";
                });
                setUserMap(map);
            } catch (error) {
                console.error("Kullanıcı listesi çekilemedi:", error);
            }
        };

        fetchUsers();

        const docRef = doc(db, "devices", id); // Koleksiyon "devices" olarak değiştirildi
        const unsubscribeDevice = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setDevice({ id: docSnap.id, ...docSnap.data() });
            } else {
                setLoading(false);
            }
        });

        const serviceRecordsQuery = query(
            collection(db, "serviceRecords"), // Koleksiyon "serviceRecords" olarak değiştirildi
            where("cihazId", "==", id), // Alan "cihazId" olarak değiştirildi
            orderBy("olusturmaTarihi", "desc") // Alan "olusturmaTarihi" olarak değiştirildi
        );

        const logsQuery = query(
            collection(db, "device_logs"),
            where("deviceId", "==", id),
            orderBy("timestamp", "desc")
        );

        const unsubscribeLogs = onSnapshot(logsQuery, (snapshot) => {
            setDeviceLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            console.error("Cihaz logları çekilirken hata:", error);
        });

        const unsubscribeServiceHistory = onSnapshot(serviceRecordsQuery, (snapshot) => {
            setServiceHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Servis geçmişi sorgusu hatası (Muhtemelen index eksik): ", error);
            setLoading(false);
        });

        return () => {
            unsubscribeDevice();
            unsubscribeServiceHistory();
            unsubscribeLogs();
        };
    }, [id]);

    if (loading) { return <Loading message="Cihaz yükleniyor..." />; }
    if (!device) { return <Typography variant="h5" align="center" sx={{ mt: 4 }}>Cihaz Bulunamadı.</Typography>; }

    return (
        <Box sx={{ p: { xs: 1, md: 2 } }}>
            {/* Header */}
            <PageHeader
                title={`Cihaz Detayı: ${device.seriNumarasi}`}
                subtitle={`${device.marka} ${device.model || ''} cihazına ait teknik detaylar ve servis geçmişi.`}
                icon={<DevicesIcon />}
            >
                {device.isHurda && <StatusChip label="HURDA" color="error" />}
                {device.isSgk && <StatusChip label="SGK ZİMMETLİ" color="warning" />}
            </PageHeader>

            <GlassCard sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>Cihaz Bilgileri</Typography>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography><b>Seri Numarası:</b> {device.seriNumarasi}</Typography>
                                <Typography><b>Marka:</b> {device.marka}</Typography>
                                <Typography><b>Model:</b> {device.model || '-'}</Typography>
                                <Typography><b>Cihaz Sınıfı:</b> {device.cihazSinifi || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography><b>Veriliş Tarihi:</b> {formatDate(device.verilisTarihi)}</Typography>
                                <Typography><b>Garanti Bitiş:</b> {formatDate(device.garantiBitis)}</Typography>
                                <Typography><b>Sonraki Bakım:</b> {formatDate(device.periyodikBakimTarihi)}</Typography>
                                <Typography><b>Müşteri:</b>
                                    <MuiLink component={RouterLink} to={`/musteri/${device.musteriId}`} sx={{ ml: 1, fontWeight: 500 }}>
                                        {device.musteriAdSoyad}
                                    </MuiLink>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </GlassCard>

            <GlassCard sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Servis Geçmişi</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Servis Tarihi</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Arıza Tanımı</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Tutar</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {serviceHistory.length > 0 ? serviceHistory.map(record => (
                                <TableRow hover key={record.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/servis-kaydi/${record.id}`)}>
                                    <TableCell>{formatDate(record.olusturmaTarihi)}</TableCell>
                                    <TableCell>{record.arizaTanimi || '-'}</TableCell>
                                    <TableCell>{record.durum}</TableCell>
                                    <TableCell align="right">{formatCurrency(record.toplamTutar)}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">Bu cihaza ait servis geçmişi bulunmamaktadır.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>

            <GlassCard sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Cihaz Geçmişi (Log Kayıtları)</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>İşlem Türü</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Açıklama</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {deviceLogs.length > 0 ? deviceLogs.map(log => (
                                <TableRow key={log.id}>
                                    <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                                    <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.75rem' }}>{log.actionType}</TableCell>
                                    <TableCell>{log.description}</TableCell>
                                    <TableCell>{userMap[log.userId] || log.userId}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">Bu cihaza ait geçmiş kaydı bulunmamaktadır.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>
        </Box>
    );
};

export default CihazDetay;